import { useMemo, useState } from 'react';
import { useInfinityProductCodesQuery } from './useInfinityProductCodesQuery';

import { Option } from 'common/components/Inputs/Autocomplete/utils/models';
import { OPTION_TYPE } from 'src/common/components/Inputs/Autocomplete/utils/schemas';

type UseProductCodesAutocompleteProps = {
  initialIds: (string | null)[];
  search: string;
  hints: string[];
  onSelectedChange: (options: Option[]) => void;
};

const blankOptionProductCodeName = 'blank';

const mapProductCodesIdsToOption = (productCodes: (string | null)[]) => {
  return productCodes.map((code) => {
    if (code === null) {
      return { id: null, name: blankOptionProductCodeName, type: OPTION_TYPE.BLANK };
    }
    return {
      id: code,
      name: code,
    };
  });
};

export const useProductCodesAutocomplete = ({
  initialIds,
  search,
  hints,
  onSelectedChange,
}: UseProductCodesAutocompleteProps) => {
  const [selectedOptions, setSelectedOptions] = useState<Option[]>(
    mapProductCodesIdsToOption(initialIds),
  );

  if (initialIds.length === 0 && selectedOptions.length !== 0) {
    setSelectedOptions([]);
  }

  const {
    productCodes: productCodesData,
    isFetched,
    ...productCodesQuery
  } = useInfinityProductCodesQuery(search, {
    minTime: 250,
  });

  const displayHints = !search;

  const optionsToReturn = useMemo(() => {
    const isBlankSearched = !!search && blankOptionProductCodeName.includes(search.trim());

    return mapProductCodesIdsToOption([...(isBlankSearched ? [null] : []), ...productCodesData]);
  }, [productCodesData]);

  const hintOptionsToReturn = useMemo(() => {
    return displayHints ? mapProductCodesIdsToOption(hints) : [];
  }, [hints, displayHints]);

  const updateSelectedOptions = (options: Option[]) => {
    setSelectedOptions(options);
    onSelectedChange(options);
  };

  return {
    options: optionsToReturn,
    hintOptions: hintOptionsToReturn,
    selectedOptions,
    updateSelectedOptions,
    isFetched,
    ...productCodesQuery,
  };
};
